import axios from 'axios';

const INITIAL_STATE = {
  spinResult: null,
  memberSpins: null,
  memberRewards: [],
  isLoading: false,
  error: null,
};

const state = () => ({ ...INITIAL_STATE });

const EMPTY_MEMBER_SPINS = { spinsLeft: 0, totalSpinsUsed: 0 };

function isMissingGachaponState(error) {
  return error?.response?.status === 404;
}

const mutations = {
  SET_SPIN_RESULT(state, result) {
    state.spinResult = result;
  },
  SET_MEMBER_SPINS(state, spins) {
    state.memberSpins = spins;
  },
  SET_MEMBER_REWARDS(state, rewards) {
    state.memberRewards = rewards;
  },
  SET_LOADING(state, loading) {
    state.isLoading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_SPIN_RESULT(state) {
    state.spinResult = null;
  },
  RESET_STATE(state) {
    Object.assign(state, INITIAL_STATE);
  },
  DECREMENT_SPINS_OPTIMISTIC(state) {
    if (state.memberSpins && state.memberSpins.spinsLeft > 0) {
      state.memberSpins.spinsLeft -= 1;
    }
  },
  INCREMENT_SPINS_ROLLBACK(state) {
    if (state.memberSpins) {
      state.memberSpins.spinsLeft = (state.memberSpins.spinsLeft || 0) + 1;
    }
  },
};

const actions = {
  async spinGachapon({ commit, rootState, state }, memberId) {
    const originalSpinsLeft = state.memberSpins?.spinsLeft ?? 0;

    try {
      commit('SET_ERROR', null);
      commit('DECREMENT_SPINS_OPTIMISTIC');

      const accessToken = rootState.user.token;
      const response = await axios.request(
        process.env.VUE_APP_API_URL + '/gachapon/spin-gachapon',
        {
          method: 'post',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          data: { memberId },
        },
      );

      commit('SET_SPIN_RESULT', response.data);
      console.log('🎰 Gachapon Spin API Response:', JSON.stringify(response.data, null, 2));
      console.log('🎰 Reward object:', JSON.stringify(response.data.reward, null, 2));
      return {
        success: true,
        spinsLeft: response.data.spinsLeft,
        reward: response.data.reward,
      };
    } catch (error) {
      console.error('Error spinning gachapon:', error);
      commit('INCREMENT_SPINS_ROLLBACK');

      const errorMessage = error.response?.data?.message || 'Failed to spin gachapon';
      commit('SET_ERROR', errorMessage);
      throw error;
    }
  },

  async getMemberSpins({ commit, rootState }) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const accessToken = rootState.user.token;
      const response = await axios.get(process.env.VUE_APP_API_URL + '/gachapon/get-member-spins', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      commit('SET_MEMBER_SPINS', response.data);
      return response.data;
    } catch (error) {
      if (isMissingGachaponState(error)) {
        commit('SET_MEMBER_SPINS', EMPTY_MEMBER_SPINS);
        commit('SET_ERROR', null);
        console.warn('[Gachapon] Member spins unavailable; defaulting to zero spins.');
        return EMPTY_MEMBER_SPINS;
      }

      console.error('Error checking member spins:', error);
      const errorMessage = error.response?.data?.message || 'Failed to check member spins';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async getMemberRewards({ commit, rootState }) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const accessToken = rootState.user.token;
      const response = await axios.get(
        process.env.VUE_APP_API_URL + '/gachapon/get-member-rewards',
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      commit('SET_MEMBER_SPINS', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting member rewards:', error);
      const errorMessage = error.response?.data?.message || 'Failed to get member rewards';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async getRewardsList({ commit, rootState }) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const accessToken = rootState.user.token;
      const response = await axios.get(process.env.VUE_APP_API_URL + '/gachapon/get-rewards-list', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      commit('SET_MEMBER_SPINS', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting rewards list:', error);
      const errorMessage = error.response?.data?.message || 'Failed to get rewards list';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  clearSpinResult({ commit }) {
    commit('CLEAR_SPIN_RESULT');
  },

  resetState({ commit }) {
    commit('RESET_STATE');
  },
};

const getters = {
  hasSpinsRemaining: state => {
    return (
      state.memberSpins &&
      (state.memberSpins.spinsLeft ?? state.memberSpins.spinsRemaining ?? 0) > 0
    );
  },
  spinsRemaining: state => {
    return state.memberSpins
      ? (state.memberSpins.spinsLeft ?? state.memberSpins.spinsRemaining ?? 0)
      : 0;
  },
  totalSpinsUsed: state => {
    return state.memberSpins ? state.memberSpins.totalSpinsUsed : 0;
  },
  lastSpinResult: state => {
    return state.spinResult;
  },
  memberRewards: state => {
    return state.memberRewards;
  },
  isLoading: state => {
    return state.isLoading;
  },
  error: state => {
    return state.error;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
